"use client";

import * as React from "react";
import { createPortal } from "react-dom";
import {
  Loader2,
  Shield,
  Eye,
  EyeOff,
  KeyRound,
  RefreshCw,
  Copy,
  Check,
  AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/toast";
import { useAuth } from "@/contexts/AuthContext";
import { useRotateBootstrapKey } from "@/hooks/useRotateBootstrapKey";
import { extractApiErrorMessage } from "@/lib/api-error";

const MIN_SECRET_LENGTH = 16;
const SECRET_LENGTH = 40;
const CHARSET =
  "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";

/**
 * Génère une clé secrète cryptographiquement sûre via Web Crypto API.
 * 40 caractères alphanumériques — robuste face aux attaques par force brute.
 */
function generateSecret(length = SECRET_LENGTH): string {
  const values = new Uint32Array(length);
  crypto.getRandomValues(values);
  let result = "";
  for (let i = 0; i < length; i++) {
    result += CHARSET[values[i] % CHARSET.length];
  }
  return result;
}

interface RotationGateModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Quand true, l'utilisateur peut annuler avant de soumettre (Escape,
   * clic sur le fond, bouton Annuler) — utilisé pour la rotation volontaire
   * depuis Paramètres > Sécurité. Une fois la rotation réussie, le modal
   * redevient non-dismissible dans tous les cas : il faut acquitter les
   * nouveaux identifiants avant de se reconnecter.
   */
  dismissible?: boolean;
}

type CopiedField = "id" | "secret" | null;

export function RotationGateModal({ isOpen, onClose, dismissible = false }: RotationGateModalProps) {
  const { logout } = useAuth();
  const { addToast } = useToast();
  const rotate = useRotateBootstrapKey();

  const [newSecret, setNewSecret] = React.useState("");
  const [showSecret, setShowSecret] = React.useState(false);
  const [copiedField, setCopiedField] = React.useState<CopiedField>(null);
  const [isSuccess, setIsSuccess] = React.useState(false);
  const [savedClientId, setSavedClientId] = React.useState("");

  const isPending = rotate.isPending;
  const canSubmit =
    newSecret.length >= MIN_SECRET_LENGTH && !isPending && !isSuccess;

  // Lock body scroll when the modal is open.
  React.useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  // Focus the input as soon as the modal mounts (only in rotation mode).
  React.useEffect(() => {
    if (!isOpen || isSuccess) return;
    const t = setTimeout(() => {
      document.getElementById("rotation-new-secret")?.focus();
    }, 50);
    return () => clearTimeout(t);
  }, [isOpen, isSuccess]);

  // Bloque Escape sauf en mode dismissible avant soumission. Une fois la
  // rotation réussie, la seule sortie est l'acquittement par l'utilisateur —
  // même en mode dismissible, on ne laisse jamais échapper les identifiants.
  React.useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (dismissible && !isSuccess) {
        onClose();
        return;
      }
      e.preventDefault();
      e.stopPropagation();
    };
    document.addEventListener("keydown", handler, true);
    return () => document.removeEventListener("keydown", handler, true);
  }, [isOpen, dismissible, isSuccess, onClose]);

  // Reset complet du state quand le modal se ferme.
  React.useEffect(() => {
    if (!isOpen) {
      setNewSecret("");
      setShowSecret(false);
      setCopiedField(null);
      setIsSuccess(false);
      setSavedClientId("");
      rotate.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen || typeof document === "undefined") return null;

  const handleGenerate = () => {
    const generated = generateSecret();
    setNewSecret(generated);
    setShowSecret(true);
    setCopiedField(null);
  };

  const handleCopyToClipboard = async (
    value: string,
    field: CopiedField,
  ) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 1500);
    } catch {
      addToast("Impossible de copier dans le presse-papiers", "error");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newSecret) {
      addToast("Veuillez définir une nouvelle clé secrète", "error");
      return;
    }
    if (newSecret.length < MIN_SECRET_LENGTH) {
      addToast(
        `La clé secrète doit contenir au moins ${MIN_SECRET_LENGTH} caractères`,
        "error",
      );
      return;
    }

    try {
      const result = await rotate.mutateAsync({ new_secret: newSecret });
      // On passe en mode "success" : le formulaire est remplacé par l'écran
      // d'affichage des credentials. Le modal ne se ferme JAMAIS sans un
      // clic explicite de l'utilisateur sur "J'ai sauvegardé".
      setSavedClientId(result.client_id);
      setShowSecret(false);
      setIsSuccess(true);
    } catch (error) {
      const message = extractApiErrorMessage(
        (error as { response?: { data?: unknown } })?.response?.data,
        "Erreur lors de la rotation de la clé",
      );
      addToast(message, "error");
    }
  };

  const handleAcknowledge = () => {
    // L'utilisateur confirme avoir sauvegardé ses identifiants :
    // on ferme le modal puis on déconnecte pour qu'il se reconnecte
    // avec les nouveaux credentials.
    onClose();
    logout();
  };

  // Par défaut, absorbe tous les events souris sur le backdrop pour empêcher
  // la fermeture. En mode dismissible (avant soumission), un clic sur le
  // fond ferme le modal comme n'importe quel dialog standard.
  const canDismiss = dismissible && !isSuccess;
  const handleBackdropClick = (e: React.SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (canDismiss) onClose();
  };

  const modal = (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="rotation-gate-title"
      onMouseDown={handleBackdropClick}
      onClick={handleBackdropClick}
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md max-h-[90vh] overflow-y-auto backdrop-blur-xl bg-card/95 border border-border/50 rounded-2xl shadow-2xl p-8 animate-in fade-in zoom-in-95 duration-200"
      >
        {/* Header — change selon l'état (rotation vs success) */}
        <div className="text-center mb-6">
          <div
            className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl shadow-lg transition-colors ${
              isSuccess
                ? "bg-linear-to-br from-emerald-500 to-emerald-600"
                : "bg-linear-to-br from-emicash-600 to-emicash-700"
            }`}
          >
            {isSuccess ? (
              <Check className="size-8 text-white" />
            ) : (
              <KeyRound className="size-8 text-white" />
            )}
          </div>
          <h1
            id="rotation-gate-title"
            className="text-2xl font-bold text-foreground mt-4 mb-2"
          >
            {isSuccess ? "Identifiants mis à jour" : "Rotation de clé requise"}
          </h1>
          <p className="text-muted-foreground text-sm">
            {isSuccess
              ? "Votre clé secrète a été rotée avec succès. Conservez ces informations en lieu sûr — elles ne seront plus jamais affichées."
              : "Pour des raisons de sécurité, vous devez définir une nouvelle clé secrète avant de continuer à utiliser le portail."}
          </p>
        </div>

        {isSuccess ? (
          /* ============ SUCCESS VIEW ============ */
          <div className="space-y-4">
            {/* Client ID */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-foreground">
                Client ID
              </Label>
              <div className="flex gap-2">
                <Input
                  readOnly
                  value={savedClientId}
                  className="font-mono text-xs cursor-text"
                  onClick={(e) => e.currentTarget.select()}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleCopyToClipboard(savedClientId, "id")}
                  className="cursor-pointer shrink-0 px-3"
                  aria-label="Copier le Client ID"
                >
                  {copiedField === "id" ? (
                    <Check className="size-4 text-emerald-600" />
                  ) : (
                    <Copy className="size-4" />
                  )}
                </Button>
              </div>
            </div>

            {/* Nouvelle clé secrète */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-foreground">
                Nouvelle clé secrète
              </Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    readOnly
                    type={showSecret ? "text" : "password"}
                    value={newSecret}
                    className="font-mono text-xs pr-10 cursor-text"
                    onClick={(e) => e.currentTarget.select()}
                  />
                  <button
                    type="button"
                    onClick={() => setShowSecret(!showSecret)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground cursor-pointer"
                    aria-label={
                      showSecret
                        ? "Masquer la clé secrète"
                        : "Afficher la clé secrète"
                    }
                    tabIndex={-1}
                  >
                    {showSecret ? (
                      <EyeOff className="size-4" />
                    ) : (
                      <Eye className="size-4" />
                    )}
                  </button>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleCopyToClipboard(newSecret, "secret")}
                  className="cursor-pointer shrink-0 px-3"
                  aria-label="Copier la clé secrète"
                >
                  {copiedField === "secret" ? (
                    <Check className="size-4 text-emerald-600" />
                  ) : (
                    <Copy className="size-4" />
                  )}
                </Button>
              </div>
            </div>

            {/* Avertissement critique */}
            <div className="rounded-xl border border-crimson-200 bg-crimson-50/80 p-4">
              <div className="flex gap-3 items-start">
                <AlertTriangle className="size-5 text-crimson-600 shrink-0 mt-0.5" />
                <div className="text-left text-sm text-crimson-900 space-y-1">
                  <p className="font-medium">Sauvegardez-les maintenant</p>
                  <p>
                    Ces identifiants ne seront plus jamais affichés nulle part.
                    Utilisez-les pour vous reconnecter au portail.
                  </p>
                </div>
              </div>
            </div>

            <Button
              type="button"
              onClick={handleAcknowledge}
              className="w-full cursor-pointer bg-linear-to-r from-emicash-500 to-emicash-600 hover:from-emicash-600 hover:to-emicash-700 text-white font-medium shadow-cta hover:shadow-glow"
            >
              J&apos;ai sauvegardé mes identifiants — Se reconnecter
            </Button>
          </div>
        ) : (
          /* ============ ROTATION FORM ============ */
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label
                htmlFor="rotation-new-secret"
                className="text-sm font-medium text-foreground"
              >
                Nouvelle clé secrète
              </Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    id="rotation-new-secret"
                    type={showSecret ? "text" : "password"}
                    value={newSecret}
                    onChange={(e) => setNewSecret(e.target.value)}
                    placeholder="Définissez ou générez votre clé"
                    disabled={isPending}
                    className="w-full pr-10"
                    autoComplete="off"
                    spellCheck={false}
                  />
                  {newSecret.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setShowSecret(!showSecret)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground cursor-pointer"
                      aria-label={
                        showSecret
                          ? "Masquer la clé secrète"
                          : "Afficher la clé secrète"
                      }
                      tabIndex={-1}
                    >
                      {showSecret ? (
                        <EyeOff className="size-4" />
                      ) : (
                        <Eye className="size-4" />
                      )}
                    </button>
                  )}
                </div>
                <Button
                  type="button"
                  onClick={() => handleCopyToClipboard(newSecret, "secret")}
                  disabled={!newSecret || isPending}
                  variant="outline"
                  className="cursor-pointer shrink-0 px-3"
                  aria-label="Copier la clé secrète"
                  tabIndex={-1}
                >
                  {copiedField === "secret" ? (
                    <Check className="size-4 text-emerald-600" />
                  ) : (
                    <Copy className="size-4" />
                  )}
                </Button>
                <Button
                  type="button"
                  onClick={handleGenerate}
                  disabled={isPending}
                  variant="outline"
                  className="cursor-pointer shrink-0"
                >
                  <RefreshCw className="size-4" />
                  <span className="hidden sm:inline">Générer</span>
                </Button>
              </div>
              <div className="flex items-center justify-between text-xs">
                <p className="text-muted-foreground">
                  Minimum {MIN_SECRET_LENGTH} caractères. Notez-la en lieu sûr.
                </p>
                {newSecret.length > 0 && (
                  <span
                    className={`font-mono ${
                      newSecret.length >= MIN_SECRET_LENGTH
                        ? "text-emerald-600"
                        : "text-amber-600"
                    }`}
                  >
                    {newSecret.length}
                  </span>
                )}
              </div>
            </div>

            <div className="rounded-xl border border-amber-200 bg-amber-50/80 p-4">
              <div className="flex gap-3 items-start">
                <Shield className="size-5 text-amber-600 shrink-0 mt-0.5" />
                <div className="text-left text-sm text-amber-900 space-y-1">
                  <p className="font-medium">Action irréversible</p>
                  <p>
                    Après validation, vous devrez utiliser votre nouvelle clé
                    secrète pour vous reconnecter.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {dismissible && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  disabled={isPending}
                  className="cursor-pointer"
                >
                  Annuler
                </Button>
              )}
              <Button
                type="submit"
                disabled={!canSubmit}
                className="flex-1 cursor-pointer bg-linear-to-r from-emicash-500 to-emicash-600 hover:from-emicash-600 hover:to-emicash-700 text-white font-medium shadow-cta hover:shadow-glow disabled:opacity-50"
              >
                {isPending ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="size-4 animate-spin" />
                    Rotation en cours…
                  </span>
                ) : (
                  "Valider"
                )}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}